import React, { useRef, useState } from 'react'
import Modal from './MyModal'
import MyButton from './buttons'
import { useTranslation } from 'react-i18next'
import { FaPhone, FaTimesCircle, FaSms } from 'react-icons/fa'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { smsService } from '../../services/smsService'
import CodeInput from './CodeInput'

interface PhoneValidationModalProps {
  isOpen: boolean
  onClose: () => void
  onCancel: () => void
  phoneNumber: string
  isPhoneConfirmed: boolean
  onConfirm: (code: string) => void
  isValidating: boolean
  validationMessage: {
    type: string
    message: string
  }
  userId: string
  userData: {
    firstname: string
    lastname: string
    birthday: string
    nickname: string
    email: string
    tva: string
    status: string[]
    address: {
      street?: string
      number?: string
      city?: string
      zip_code?: string
    }
  }
}

const PhoneValidationModal: React.FC<PhoneValidationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  userId,
  phoneNumber,
  isValidating,
  validationMessage,
  userData,
}) => {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [, setOtpSid] = useState('')
  const [isRequestingOTP, setIsRequestingOTP] = useState(false)
  const [isSMSSent, setIsSMSSent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const steps = [
    { number: 1, text: t('step1-activate-profile') },
    { number: 2, text: t('step2-verify-number') },
    { number: 3, text: t('step3-receive-sms') },
    { number: 4, text: t('step4-enter-code') },
  ]

  const handleSendSMS = async () => {
    setError('')

    try {
      setIsRequestingOTP(true)

      const response = await smsService.requestOTP(phoneNumber, {
        ...userData,
        userId,
      })

      if (response?.otpSid) {
        setOtpSid(response.otpSid)
        setIsSMSSent(true)
        setCode('')
      } else {
        setError(t('error-sending-sms'))
      }
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === 'Failed to fetch phone numbers'
      ) {
        setError(t('error-checking-phone'))
      } else {
        setError(t('error-sending-sms'))
      }
      console.error('Error:', err)
    } finally {
      setIsRequestingOTP(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 6)
    setCode(numericValue)
    setError('')
  }

  const handleSubmit = async () => {
    if (isSubmitting) return
    if (code.trim() === '') {
      setError(t('verification-code-required'))
      return
    }

    if (code.length !== 6) {
      setError(t('code-must-be-6-digits'))
      return
    }

    try {
      setIsSubmitting(true)
      const verificationSid = localStorage.getItem('verificationSid')

      if (!verificationSid) {
        setError(t('verification-sid-missing'))
        return
      }

      await onConfirm(code)
    } catch (err: any) {
      console.error('Erreur lors de la validation OTP:', err)
      setError(err.message || t('error-verifying-code'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setCode('')
    setError('')
    setIsSMSSent(false)
    onCancel()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} disableOverlayClick={true}>
      <div className='w-full mx-auto transform transition-all max-h-[90vh] overflow-y-auto'>
        <div className='bg-anthracite rounded-t-lg px-6 py-4 flex items-center justify-between sticky top-0 z-10'>
          <div className='flex items-center space-x-3'>
            <div className='bg-redpink p-2 rounded-full'>
              <FaPhone className='text-white text-lg sm:text-xl' />
            </div>
            <h2 className='text-lg sm:text-xl font-semibold text-white'>
              {t('phone-verification')}
            </h2>
          </div>
        </div>

        <div className='bg-white rounded-b-lg px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-6 relative'>
          {(isValidating || isRequestingOTP) && (
            <div className='absolute inset-0 bg-white/90 flex items-center justify-center z-20 rounded-b-lg'>
              <div className='text-center'>
                <Loader2 className='w-10 h-10 text-redpink animate-spin mx-auto mb-3' />
                <p className='text-gray-600'>
                  {isRequestingOTP ? t('sending-sms') : t('validating')}
                </p>
              </div>
            </div>
          )}

          <div className='bg-grisclair rounded-lg p-3 sm:p-4'>
            <div className='space-y-3'>
              {steps.map(step => (
                <div
                  key={step.number}
                  className='flex items-start space-x-3 text-left'
                >
                  <div className='bg-redpink rounded-full min-w-[24px] h-6 flex items-center justify-center text-white text-sm'>
                    {step.number}
                  </div>
                  <span className='text-sm text-anthracite pt-0.5'>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className='bg-grisclair rounded-lg p-3 sm:p-4'>
            <div className='flex flex-col sm:flex-row items-center justify-between gap-3'>
              <div className='flex items-center space-x-2'>
                <FaPhone className='text-redpink text-xl sm:text-2xl' />
                <span className='font-medium'>{phoneNumber}</span>
              </div>
              <div className='flex items-center space-x-3'>
                <MyButton
                  onClick={handleSendSMS}
                  variant='blue'
                  className='w-full sm:w-auto flex items-center justify-center'
                  disabled={isValidating || isRequestingOTP}
                >
                  <FaSms className='mr-2 text-xl' />
                  {isSMSSent ? t('resend-sms') : t('send-sms')}
                </MyButton>
              </div>
            </div>
          </div>

          <>
            <div className='space-y-3 sm:space-y-4'>
              <div className='bg-grisclair rounded-lg p-4'>
                <p className='text-sm text-gray-600 mb-4'>
                  {t('enter-6-digit-code')}
                </p>

                <CodeInput
                  length={6}
                  value={code}
                  onChange={handleCodeChange}
                />
              </div>
              {(error || validationMessage.message) && (
                <div
                  className={`rounded-lg p-2 flex items-center space-x-2 ${
                    error
                      ? 'bg-redpink/10 text-redpink'
                      : validationMessage.type === 'success'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-redpink/10 text-redpink'
                  }`}
                >
                  {error ? (
                    <FaTimesCircle className='w-5 h-5' />
                  ) : validationMessage.type === 'success' ? (
                    <CheckCircle2 className='w-5 h-5' />
                  ) : (
                    <XCircle className='w-5 h-5' />
                  )}
                  <p className='text-sm font-medium'>
                    {error || validationMessage.message}
                  </p>
                </div>
              )}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <MyButton
                onClick={handleCancel}
                variant='anthracite-redpink'
                className='w-full px-3 py-1.5 text-sm flex items-center justify-center'
                disabled={isValidating || isRequestingOTP}
              >
                <XCircle className='w-4 h-4 mr-1.5' />
                {t('cancel')}
              </MyButton>
              <MyButton
                onClick={handleSubmit}
                variant='green-gray'
                className='w-full px-3 py-1.5 text-sm flex items-center justify-center'
                disabled={
                  isValidating ||
                  isRequestingOTP ||
                  code.length !== 6 ||
                  isSubmitting
                }
              >
                <CheckCircle2 className='w-4 h-4 mr-1.5' />
                {t('confirm')}
              </MyButton>
            </div>
          </>
        </div>
      </div>
    </Modal>
  )
}

export default PhoneValidationModal
